import React, { useState, useEffect, useCallback, memo } from "react";
import { Card, Select, Button, Table, Modal, Form, Input, message, Space, Tag, Tooltip } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  getOccurrences,
  getOccurrenceTypes,
  getAllStatus,
  addOccurrence,
  updateOccurrence,
  deleteOccurrence,
} from "./periodicalApi";

const { Option } = Select;
const { confirm } = Modal;

// "Accurence" (sic, matching the old app's own naming) = the frequency master
// list — e.g. Daily/Weekly/Monthly recurrence definitions that periodicals
// reference. Field names (name, description, occur_frequency, status)
// confirmed by reading MajesticERP-PeridicalAPI/code/controllers/OccurrencesController.js's
// joi validation schemas directly.
const OccurrenceFormModal = ({ visible, editing, occurrenceTypes, statuses, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        name: editing?.name || "",
        description: editing?.description || "",
        occur_frequency: editing?.occur_frequency,
        status: editing?.status,
      });
    }
  }, [visible, editing, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const { error, result } = editing
        ? await updateOccurrence({ ...values, id: editing.id })
        : await addOccurrence({ name: values.name, description: values.description, occur_frequency: values.occur_frequency });
      if (error) message.error(result?.message || "Failed to save.");
      else {
        message.success(editing ? "Updated." : "Added.");
        onSaved();
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={editing ? "Edit Frequency" : "Add Frequency"} visible={visible} onCancel={onClose} onOk={handleOk} confirmLoading={saving} destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="Name" rules={[{ required: true, message: "Please enter the name." }]}>
          <Input />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="occur_frequency" label="Occurs" rules={[{ required: true, message: "Please select how often this recurs." }]}>
          <Select placeholder="Select frequency">
            {occurrenceTypes.map((t) => <Option key={t.id} value={t.id}>{t.name}</Option>)}
          </Select>
        </Form.Item>
        {editing && (
          <Form.Item name="status" label="Status" rules={[{ required: true, message: "Please select a status." }]}>
            <Select placeholder="Select status">
              {statuses.map((s) => <Option key={s.id} value={s.id}>{s.name}</Option>)}
            </Select>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

const Accurence = () => {
  const [rows, setRows] = useState([]);
  const [occurrenceTypes, setOccurrenceTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formModal, setFormModal] = useState({ visible: false, editing: null });

  const load = useCallback(async () => {
    setLoading(true);
    const [rowsRes, typesRes, statusRes] = await Promise.all([getOccurrences(), getOccurrenceTypes(), getAllStatus()]);
    setRows(!rowsRes.error && Array.isArray(rowsRes.result?.result) ? rowsRes.result.result : []);
    setOccurrenceTypes(!typesRes.error && Array.isArray(typesRes.result?.result) ? typesRes.result.result : []);
    setStatuses(!statusRes.error && Array.isArray(statusRes.result?.result) ? statusRes.result.result : []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const typeName = (id) => occurrenceTypes.find((t) => t.id === id)?.name || id;
  const statusMeta = (id) => statuses.find((s) => s.id === id);

  const handleDelete = useCallback((row) => {
    confirm({
      title: "Delete frequency",
      content: `Delete "${row.name}"?`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        const { error, result } = await deleteOccurrence(row.id);
        if (error) message.error(result?.message || "Failed to delete.");
        else { message.success("Deleted."); load(); }
      },
    });
  }, [load]);

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Description", dataIndex: "description", key: "description" },
    { title: "Occurs", dataIndex: "occur_frequency", key: "occur_frequency", render: typeName },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (id) => <Tag>{statusMeta(id)?.name || id}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, row) => (
        <Space>
          <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => setFormModal({ visible: true, editing: row })} type="primary" aria-label="Edit" /></Tooltip>
          <Tooltip title="Delete"><Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(row)} aria-label="Delete" /></Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ textAlign: "right", marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setFormModal({ visible: true, editing: null })}>Add Frequency</Button>
      </div>
      <Card>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: "No frequencies defined yet." }}
        />
      </Card>

      <OccurrenceFormModal
        visible={formModal.visible}
        editing={formModal.editing}
        occurrenceTypes={occurrenceTypes}
        statuses={statuses}
        onClose={() => setFormModal({ visible: false, editing: null })}
        onSaved={load}
      />
    </div>
  );
};

export default memo(Accurence);
